import {useCallback, useMemo} from 'react';
import {addDoc, collection, deleteDoc, deleteField, doc, getDoc, query, updateDoc, where} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {Homework, HomeworkInsertOptions, HomeworkUpdateOptions} from '../types/Homework';
import {MONGO_DATE_TIME_FORMAT} from "../utils/date";
import moment from "moment";
import {firestore} from "../App";
import {processRemindAt, processSteps} from "./common/event";

interface UseHomeworkManagerProps {
    plannerId: string;
}

export function useHomeworkManager({plannerId}: UseHomeworkManagerProps) {

    const homeworkQuery = useMemo(() => {
        if (!plannerId) return null;

        return query(
            collection(firestore, 'syncedPlanners', plannerId, 'events'),
            where("eventKind", "==", "homework")
        );
    }, [plannerId]);

    const [homeworkSnapshot, loadingHomework, errorHomework] = useCollection(homeworkQuery);

    const homework = useMemo(() => {
        return homeworkSnapshot?.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Homework)) || [];
    }, [homeworkSnapshot]);

    /**
     * Inserts a homework document in the database.
     */
    const createHomework = useCallback(async (options: HomeworkInsertOptions) => {
        if (!plannerId) return;

        const homeworkDoc: any = {
            eventKind: 'homework',
            plannerId: plannerId,
            subjectId: options.subjectId,
            title: options.title,
            date: options.date,
            isArchived: options.isArchived,
            note: options.note,
            completedOn: undefined,
            ...processRemindAt(options.remindAt),
            ...processSteps(options.steps),
            createdOn: moment().format(MONGO_DATE_TIME_FORMAT)
        };

        // Firestore does not allow 'undefined' values.
        Object.keys(homeworkDoc).forEach(key => {
            if (homeworkDoc[key] === undefined) {
                delete homeworkDoc[key];
            }
        });

        const colRef = collection(firestore, 'syncedPlanners', plannerId, 'events');

        try {
            console.log("Creating Homework: ", homeworkDoc);
            const docRef = await addDoc(colRef, homeworkDoc);
            console.log("Homework created successfully with ID: ", docRef.id);
            return docRef.id;
        } catch (error) {
            console.error("Error creating Homework: ", error);
        }
    }, [plannerId]);

    /**
     * Updates a homework document in the database.
     */
    const updateHomework = useCallback(async (homeworkId: string, options: HomeworkUpdateOptions) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'events', homeworkId);

        const updateData: { [key: string]: any } = {
            subjectId: options.subjectId ?? deleteField(),
            title: options.title,
            date: options.date,
            isArchived: options.isArchived,
            note: options.note ?? deleteField(),
            completedOn: options.completedOn ?? deleteField(),
            ...processRemindAt(options.remindAt),
            ...processSteps(options.steps)
        };

        try {
            console.log("Updating Homework: ", updateData);
            await updateDoc(docRef, updateData);
            console.log("Homework created updated with ID: ", docRef.id);
            return docRef.id;
        } catch (error) {
            console.error("Error updating Homework: ", error);
        }
    }, [plannerId]);

    /**
     * Toggles the completed_on property of a homework.
     */
    const toggleHomeworkCompletedOn = useCallback(async (homeworkId: string) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'events', homeworkId);

        try {
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
                console.error("Homework document not found");
                return;
            }

            const currentHomework = docSnap.data() as Homework;
            const newCompletedOn = currentHomework.completedOn
                ? deleteField()
                : moment().format(MONGO_DATE_TIME_FORMAT);

            await updateDoc(docRef, {completedOn: newCompletedOn});

        } catch (error) {
            console.error("Error toggling Homework: ", error);
        }
    }, [plannerId]);

    /**
     * Deletes a homework from the database.
     */
    const deleteHomework = useCallback(async (homeworkId: string) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'events', homeworkId);
        await deleteDoc(docRef);
    }, [plannerId]);

    return {
        homework,
        loadingHomework,
        errorHomework,
        createHomework,
        updateHomework,
        toggleHomeworkCompletedOn,
        deleteHomework
    };
}